import { useState } from "react";
import { CheckOutlined } from "@ant-design/icons";
import CopyChatMsg from "../../../../../assets/icons/CopyChatMsg";
import DownloadChatMsg from "../../../../../assets/icons/DownloadChatMsg";
import { type CodeHeaderProps } from "@assistant-ui/react-markdown";
import { copyToClipboard } from "helpers/utility/utilities";
import { downloadFile } from "helpers/utility/downloadFile";
import { LANGUAGE_EXTENSIONS } from "../../constants";
import styles from "./CodeHeader.module.scss";

/**
 * CodeHeader component with copy/download buttons.
 * Displayed above code blocks, shows language label and action buttons.
 */
export default function CodeHeader({ language, code }: CodeHeaderProps) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    if (!code || isCopied) return;
    try {
      await copyToClipboard(code);
      setIsCopied(true);
    } catch (e) {
      console.error("Copy failed:", e);
    }
  };

  const handleAnimationEnd = () => {
    setIsCopied(false);
  };

  const handleDownload = () => {
    if (!code) return;

    // Determine file extension from language
    const ext = LANGUAGE_EXTENSIONS[language || ""] || language || "txt";
    const suggestedName = `code.${ext}`;
    const fileName = window.prompt("Enter file name", suggestedName);

    if (!fileName) return;

    // Use utility for clean download handling
    downloadFile(fileName, {
      type: "data",
      data: new Blob([code], { type: "text/plain" }),
    });
  };

  return (
    <div className={styles.codeHeader}>
      <span className={styles.codeLanguage}>{language || "code"}</span>
      <div className={styles.codeActions}>
        <button
          className={styles.codeButton}
          onClick={handleDownload}
          title="Download"
          type="button"
        >
          <DownloadChatMsg />
        </button>
        <button
          className={styles.codeButton}
          onClick={handleCopy}
          title={isCopied ? "Copied!" : "Copy"}
          type="button"
        >
          {isCopied ? (
            <CheckOutlined
              className={styles.copied}
              onAnimationEnd={handleAnimationEnd}
            />
          ) : (
            <CopyChatMsg />
          )}
        </button>
      </div>
    </div>
  );
}
